import { Link } from "@/components/LocalizedLink";
import { ArrowRight, CheckCircle2, ExternalLink, BookOpen, Sparkles } from "lucide-react";
import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { Disclaimer } from "./Disclaimer";
import { FREE_CTA, PREMIUM_CTA } from "@/lib/cta";
import { track } from "@/lib/analytics";

export type QaPageProps = {
  question: string;
  shortAnswer: string;
  body: string;
  source: { label: string; href: string };
  extraSources?: { label: string; href: string }[];
  deepGuide: { to: string; label: string };
  related?: { to: string; label: string }[];
  breadcrumbs: Crumb[];
  lastReviewed?: string;
  /** Valfritt ämne, används i den inline-CTA som ligger direkt efter svaret. */
  topic?: string;
  /** Valfri jämförelsetabell som visas direkt under det korta svaret. */
  table?: {
    caption: string;
    headers: string[];
    rows: string[][];
  };
  /** Valfritt interaktivt innehåll som visas efter brödtexten. */
  children?: React.ReactNode;
};



export function QaPage({
  question,
  shortAnswer,
  body,
  source,
  extraSources = [],
  deepGuide,
  related = [],
  breadcrumbs,
  lastReviewed,
  topic,
  table,
  children,
}: QaPageProps) {



  return (
    <article className="container-narrow py-12 sm:py-16">
      <Breadcrumbs items={breadcrumbs} />

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex rounded-full bg-secondary px-3 py-1 text-xs">Vanlig fråga</span>
        <span className="inline-flex rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
          Uppdaterad för 2026
        </span>
        {lastReviewed && (
          <span className="inline-flex rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
            Granskad <time dateTime={lastReviewed}>{lastReviewed}</time>
          </span>
        )}
      </div>

      <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-balance">
        {question}
      </h1>

      <aside className="mt-6 rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/10 to-primary/5 p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-accent font-medium">
          <Sparkles className="h-4 w-4" /> Kort svar
        </div>
        <p className="mt-2 text-base font-medium leading-relaxed">{shortAnswer}</p>
      </aside>

      {table && (
        <section className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold">{table.caption}</h2>
          <table className="mt-3 w-full border-collapse text-sm">
            <thead>
              <tr className="text-left">
                {table.headers.map((h) => (
                  <th key={h} className="border-b border-border pb-2 pr-4 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row) => (
                <tr key={row.join("|")} className="align-top">
                  {row.map((cell, i) => (
                    <td
                      key={i}
                      className={`border-b border-border/60 py-2 pr-4 ${i === 0 ? "font-medium" : "text-muted-foreground"}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Kontextuell CTA direkt efter svaret – där läsarens intention är högst. */}
      <div className="mt-4 rounded-xl border border-border bg-card p-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
        <p className="text-sm text-muted-foreground">
          {topic
            ? `Sådana frågor om ${topic} kommer på kunskapsprovet. Testa dig direkt – 5 frågor gratis.`
            : "Den här typen av fråga kommer på kunskapsprovet. Testa dig direkt – 5 frågor gratis."}
        </p>
        <Link
          to={FREE_CTA.to}
          onClick={() => track("QA Inline CTA", { question: question.slice(0, 80) })}
          className="mt-3 sm:mt-0 inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Öva på frågan <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-8 prose prose-sm sm:prose-base max-w-none text-muted-foreground leading-relaxed whitespace-pre-line">
        {body}
      </div>

      {children}




      <section className="mt-10 rounded-2xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <BookOpen className="h-4 w-4 text-accent" /> Officiell källa
        </h2>
        <ul className="mt-3 space-y-2 text-sm">
          <li>
            <a href={source.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-accent hover:underline">
              <ExternalLink className="h-4 w-4" /> {source.label}
            </a>
          </li>
          {extraSources.map((s) => (
            <li key={s.href}>
              <a href={s.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-accent hover:underline">
                <ExternalLink className="h-4 w-4" /> {s.label}
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Källan kan ändras – verifiera alltid aktuell text hos respektive myndighet eller kommun.
        </p>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-sm font-semibold">Läs mer – djupgående guide</h2>
        <Link to={deepGuide.to} className="mt-2 inline-flex items-center gap-2 text-accent hover:underline">
          <ArrowRight className="h-4 w-4" /> {deepGuide.label}
        </Link>
      </section>

      {related.length > 0 && (
        <section className="mt-6 rounded-2xl border border-border bg-card p-6">
          <h2 className="text-sm font-semibold">Andra vanliga frågor</h2>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {related.map((r) => (
              <li key={r.to}>
                <Link to={r.to} className="inline-flex items-center gap-2 text-accent hover:underline text-sm">
                  <ArrowRight className="h-4 w-4" /> {r.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Ett enda primärt köpblock i botten – tydlig hierarki istället för två likvärdiga knappar. */}
      <div className="mt-10 rounded-2xl bg-surface-deep text-primary-foreground p-8 text-center">
        <h2 className="font-display text-2xl font-bold">Klara kunskapsprovet på första försöket</h2>
        <p className="mt-2 text-primary-foreground/80">
          800 frågor, full provsimulering och förklaringar med lagrum. Engångsbetalning från 199 kr – inget abonnemang.
        </p>
        <div className="mt-5">
          <Link
            to={PREMIUM_CTA.to}
            onClick={() => track("QA Bottom CTA", { question: question.slice(0, 80) })}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-base font-semibold text-primary hover:bg-white/90"
          >
            {PREMIUM_CTA.label} <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
        <p className="mt-4 text-sm text-primary-foreground/70">
          Vill du testa först?{" "}
          <Link to={FREE_CTA.to} className="inline-flex items-center gap-1 underline underline-offset-2 hover:no-underline">
            Kör 5 frågor gratis <CheckCircle2 className="h-3.5 w-3.5" />
          </Link>
        </p>
      </div>


      <div className="mt-10">
        <Disclaimer />
      </div>
    </article>
  );
}

export function qaJsonLd(args: { url: string; question: string; answer: string; datePublished?: string }) {
  const author = {
    "@type": "Organization",
    name: "Alkoholprovet",
    url: "https://alkoholprovet.se",
  };

  function ensureDateTime(value?: string) {
    const raw = value ?? "2025-01-15";
    const normalized = /T/.test(raw) ? raw : `${raw}T00:00:00+01:00`;
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return "2025-01-15T00:00:00+01:00";
    }
    return parsed.toISOString();
  }

  const datePublished = ensureDateTime(args.datePublished);

  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "QAPage",
      inLanguage: "sv-SE",
      mainEntity: {
        "@type": "Question",
        name: args.question,
        url: args.url,
        answerCount: 1,
        author,
        datePublished,
        acceptedAnswer: {
          "@type": "Answer",
          text: args.answer,
          url: args.url,
          upvoteCount: 1,
          author,
          datePublished,
        },
      },
    }),
  };
}
